import Tooltip from "@mui/material/Tooltip";
import moment from "moment";
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { IUserInfo } from "@hero/shared/models/userInfo";
import { IWorkingTimeUploadDay } from "@hero/shared/models/workingTimeUploadDay";
import { CalendarDayStatus, getUploadDayStatus, listDayKeys, toDayKey } from "@hero/shared/utils/working-time-upload";
import { IWorkingTimeUploadState } from "../../../redux/reducers/workingTimeUpload.reducer";
import "./styles.scss";

const STATUS_LABEL: Record<CalendarDayStatus, string> = {
    loaded: "Đã load",
    empty: "Trống",
    missing: "Sót",
    off: "Nghỉ",
    future: "Tương lai",
};
const WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

const UploadStatusCalendar: FC<{ startTime: string; endTime: string }> = ({ startTime, endTime }) => {
    const { uploadDays, holidays, complementDays }: IWorkingTimeUploadState = useSelector(
        (state: any) => state.workingTimeUpload
    );
    const allUsers: IUserInfo[] = useSelector((state: any) => state.userInfoState.userInfos);
    const todayKey = toDayKey(new Date());
    const validRange = !!startTime && !!endTime && startTime <= endTime;

    const markerByDay = useMemo(() => {
        const map: Record<string, IWorkingTimeUploadDay> = {};
        uploadDays.forEach((d) => (map[toDayKey(d.date)] = d));
        return map;
    }, [uploadDays]);

    const statusByDay = useMemo(() => {
        const map: Record<string, CalendarDayStatus> = {};
        if (!validRange) return map;
        listDayKeys(startTime, endTime).forEach(
            (key) => (map[key] = getUploadDayStatus(key, markerByDay[key], holidays, complementDays, todayKey))
        );
        return map;
    }, [validRange, startTime, endTime, markerByDay, holidays, complementDays, todayKey]);

    const months = useMemo(() => {
        const result: string[] = [];
        if (!validRange) return result;
        const cursor = moment(startTime, "YYYY-MM-DD").startOf("month");
        const end = moment(endTime, "YYYY-MM-DD");
        while (cursor.isSameOrBefore(end, "month")) {
            result.push(cursor.format("YYYY-MM"));
            cursor.add(1, "month");
        }
        return result;
    }, [validRange, startTime, endTime]);

    const statuses = Object.values(statusByDay);
    const count = (s: CalendarDayStatus) => statuses.filter((x) => x === s).length;
    const missingDays = Object.keys(statusByDay).filter((k) => statusByDay[k] === "missing");

    const getUserName = (id: string) => {
        const user = allUsers.find((u) => u._id === id);
        return user ? `${user.lastName} ${user.firstName}` : id;
    };
    const getTooltip = (key: string) => {
        const status = statusByDay[key];
        const marker = markerByDay[key];
        if (!status) return "";
        if (!marker) return STATUS_LABEL[status];
        return [
            STATUS_LABEL[status],
            `${marker.recordCount} bản ghi`,
            marker.fileName,
            getUserName(marker.uploadedBy),
            moment(marker.uploadedAt).format("DD/MM/YYYY HH:mm"),
        ]
            .filter(Boolean)
            .join(" · ");
    };

    if (!validRange) return null;
    return (
        <div className="upload-status-calendar">
            <div className="usc-summary">
                <strong>Trạng thái upload:</strong>
                <span className="usc-chip loaded">Đã load {count("loaded")}</span>
                <span className="usc-chip empty">Trống {count("empty")}</span>
                <span className="usc-chip missing">Sót {count("missing")}</span>
                {missingDays.length > 0 && (
                    <span className="usc-missing">
                        : {missingDays.map((k) => moment(k, "YYYY-MM-DD").format("DD/MM")).join(", ")}
                    </span>
                )}
            </div>
            <div className="usc-months">
                {months.map((month) => {
                    const first = moment(month, "YYYY-MM");
                    const blanks = first.isoWeekday() - 1;
                    const days = Array.from({ length: first.daysInMonth() }, (_, i) =>
                        first.clone().date(i + 1).format("YYYY-MM-DD")
                    );
                    return (
                        <div className="usc-month" key={month}>
                            <div className="usc-month-title">Tháng {first.format("MM/YYYY")}</div>
                            <div className="usc-grid">
                                {WEEKDAYS.map((w) => (
                                    <div className="usc-weekday" key={w}>
                                        {w}
                                    </div>
                                ))}
                                {Array.from({ length: blanks }, (_, i) => (
                                    <div key={"blank" + i} />
                                ))}
                                {days.map((key) => (
                                    <Tooltip key={key} title={getTooltip(key)} arrow>
                                        <div className={`usc-day ${statusByDay[key] ?? "outside"}`}>
                                            {moment(key, "YYYY-MM-DD").date()}
                                        </div>
                                    </Tooltip>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="usc-legend">
                {(Object.keys(STATUS_LABEL) as CalendarDayStatus[]).map((s) => (
                    <span key={s}>
                        <i className={`usc-day ${s}`} /> {STATUS_LABEL[s]}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default UploadStatusCalendar;
